import { html, css, LitElement } from 'lit-element';
import { db, functions } from './intrivity_app.js'; // Ensure this exports your Firestore instance
import { collection, getDocs, getDoc, doc, query, setDoc, deleteDoc, updateDoc, where } from "firebase/firestore";
import { httpsCallable } from 'firebase/functions';
import uuidv4 from 'v4-uuid';
import './intrivity-admin-invitations.js';
import './intrivity-admin-teams.js';
import './intrivity-admin-users.js';
import './intrivity-user-search.js';
import './intrivity-invitation-template-dialog.js';

// Import necessary MWC components
import '@material/mwc-button';
import '@material/mwc-dialog';
import '@material/mwc-textfield';
import '@material/mwc-list/mwc-list.js';
import '@material/mwc-list/mwc-list-item.js';
import '@material/mwc-snackbar';

import { INTRIVITY_CLIENTS_GROUPS_CLOSED } from './intrivity-admin-clients.js';
import { a11yStyles, disclosureButton, uniqueId } from './a11y.js';
import { t, Localized, formatDate, formatNumber, getLocale, LOCALES } from './i18n.js';
import {
    emitEvent, loadJSONFromStorage, getJSONFromStorage, getScore, hasRole,
    INTRIVITY_LOAD_RESOURCES, INTRIVITY_LOAD_CLIENTS, INTRIVITY_LOAD_GROUPS,
    INTRIVITY_GROUP_REPORTS_LOADED, INTRIVITY_GOOGLE_LOGGED_IN,
    INTRIVITY_GOOGLE_LOGGED_OUT, INTRIVITY_LOGIN, INTRIVITY_LOGGED_IN,
    INTRIVITY_LOGOUT, INTRIVITY_NAV_SELECT, INTRIVITY_PRINTABLE_TOGGLE,
    INTRIVITY_GROUP_SELECT, INTRIVITY_MASQUERADE,
    fetchStorageJson, buildCombinedReport, buildCombinedGroupReport, buildTeamMembers,
    AI_SUMMARY_PRINT_STYLE, buildAiSummarySectionHtml, escapeHtml, isFlagOn, openPrintWindow,
    resolvePdfMode, generateAndOpenSummaryPdf, generateAndDownloadSummaryZip, PDF_MODE_BROWSER, PDF_MODE_BACKEND
} from './utility.js'
import { recordAudit, AUDIT } from './audit_log.js';

// How many generateAiSummary calls to run concurrently during bulk
// generation. Bounded rather than unbounded so a large group doesn't blow
// past the Gemini API's requests-per-minute quota; tune based on the
// project's actual quota if generation runs start failing with rate-limit
// errors.
const AI_SUMMARY_CONCURRENCY = 8;

export class IntrivityAdminGroups extends Localized(LitElement) {
  static get properties() {
    return {
      groups: { type: Array },
      user_profile: { type: Object },
      mode: { type: String },
      client: { type: Object },
      _isDialogOpen: { type: Boolean },
      _editingGroup: { type: Object },
      _groupFormData: { type: Object },
      _isLoading: { type: Boolean },
      _errorMessage: { type: String },
      _feedbackMessage: { type: String },
      _selectedGroup: { type: Object },
      _selectedUsersGroup: { type: Object },
      _groupUuid: { type: String },             // selected group for invitations
      _expanded: { type: Boolean, state: true }, // trainer/pm accordion state
      _bulkGeneratingUuid: { type: String, state: true }, // group.uuid currently generating summaries for
      _clearingCacheUuid: { type: String, state: true },  // group.uuid whose AI cache is being cleared
      _clientMeta: { type: Object, state: true }, // clientid -> { ai_enabled, pdf_mode }, cached for the AI cascade check and PDF mode
      _templateDialogOpen: { type: Boolean, state: true },
      _templateGroup: { type: Object, state: true },
      // { group, results } awaiting a combined-PDF-vs-ZIP choice from the
      // user, once bulk generation (PDF_MODE_BACKEND clients only) has
      // finished; null when no choice is pending.
      _pdfChoicePending: { type: Object, state: true }
    };
  }

  constructor() {
    super();
    this.groups = [];
    this.mode = 'client';
    this._isDialogOpen = false;
    this._editingGroup = null;
    this._groupFormData = this._getEmptyGroupData();
    this._bulkGeneratingUuid = null;
    this._clearingCacheUuid = null;
    this._isLoading = false;
    this._errorMessage = '';
    this._feedbackMessage = '';
    this._clientMeta = {};
    this._templateDialogOpen = false;
    this._templateGroup = null;
    this._pdfChoicePending = null;
    // Collapsed by default, same as CONFIGURATION/CLIENTS/USERS.
    this._expanded = false;
  }

  updated(changedProps) {
    if (changedProps.has('groups') || changedProps.has('client')) {
      this._loadClientMeta();
    }
  }

  // Caches each referenced client's ai_enabled flag and pdf_mode so the
  // "Generate All Summaries" button (and the PM edit dialog's AI toggle) can
  // synchronously decide, without an async fetch inside render(). Reuses
  // this.client when it's already loaded (client mode) instead of
  // re-fetching it.
  async _loadClientMeta() {
    const ids = new Set(
        this.groups.filter(g => g.type === 'top').map(g => g.clientid).filter(Boolean));
    if (this.client && this.client.uuid) ids.add(this.client.uuid);

    const missing = [...ids].filter(id => !(id in this._clientMeta));
    if (missing.length === 0) return;

    const updates = {};
    await Promise.all(missing.map(async (id) => {
      if (this.client && this.client.uuid === id) {
        updates[id] = { ai_enabled: isFlagOn(this.client.ai_enabled), pdf_mode: resolvePdfMode(this.client) };
        return;
      }
      try {
        const snap = await getDoc(doc(db, 'clients', id));
        const data = snap.exists() ? snap.data() : null;
        updates[id] = { ai_enabled: isFlagOn(data && data.ai_enabled), pdf_mode: resolvePdfMode(data) };
      } catch (e) {
        console.warn(`Could not load client metadata for ${id}:`, e);
        updates[id] = { ai_enabled: false, pdf_mode: PDF_MODE_BROWSER };
      }
    }));
    this._clientMeta = { ...this._clientMeta, ...updates };
  }

  // Top-level groups only (no parent to consider) — whether the client and
  // the group itself both have AI features enabled.
  _aiEnabledForGroup(group) {
    const meta = this._clientMeta[group.clientid];
    return !!(meta && meta.ai_enabled && isFlagOn(group.ai_enabled));
  }

  _getEmptyGroupData() {
    return {
      uuid: uuidv4(),
      type: 'top',
      survey_monkey_key: 'LWV5WDJ',
      status: 'none',
      payment_amount: 0.00,
      payment_required: 0,
      name: '',
      description: '',
      code: this._generateRandomCode(),
      clientid: this.client ? this.client.uuid: '',
      joining: 1,
      report: 1,
      group_compare: 1,
      norm_groups: 0,
      group_enabled: 1,
      printable_report: 1,
      disable_report_privacy: 0,
      ai_enabled: 0,
      restrict_team_comparison: 0
    };
  }

  // Trainers, program managers, client managers, and admins can all flip
  // this without needing full edit access to the group/team.
  async _toggleReportPrivacy(group) {
    const newValue = group.disable_report_privacy ? 0 : 1;
    await updateDoc(doc(db, "groups", group.uuid), { disable_report_privacy: newValue });
    group.disable_report_privacy = newValue;
    this.requestUpdate();
    // A cached summary was generated under the old privacy setting -- e.g.
    // it may already have real teammate names baked in from before privacy
    // was turned on. Toggling the flag alone doesn't change that content,
    // so clear the cache and let it regenerate under the new setting.
    await this._clearAiSummariesForGroup(group.uuid);
  }

  // Deletes every direct member's cached AI Summary for this group, so the
  // next request regenerates it -- used whenever a change to the group
  // (privacy, due date) would make an already-cached summary's content
  // stale or wrong. Mirrors intrivity-admin-teams.js's
  // _clearAiSummariesForTeam.
  async _clearAiSummariesForGroup(groupUuid) {
    try {
      const usersRef = collection(db, "users");
      const q = query(usersRef, where("groups", "array-contains", groupUuid));
      const snap = await getDocs(q);
      await Promise.all(snap.docs.map((userDoc) =>
          deleteDoc(doc(db, "users", userDoc.id, "ai_summaries", groupUuid))
      ));
      console.log(`Cleared cached AI summaries for group ${groupUuid} (${snap.docs.length} member(s)).`);
      return snap.docs.length;
    } catch (error) {
      console.error("Error clearing AI summaries for group change:", error);
      return null;   // null means it failed; existing callers ignore this
    }
  }

  // Clears the cached AI Summaries for a group AND for each of its teams.
  //
  // The two caches are separate: a summary is stored at
  // users/{email}/ai_summaries/{cohortUuid}, keyed by whichever cohort it was
  // generated for, and _clearAiSummariesForGroup only matches members of that
  // one uuid. Clearing a top-level group therefore leaves its teams' summaries
  // untouched, which is rarely what an admin means -- so this does both.
  async _clearAiCacheForGroupAndTeams(group) {
    // Query for the teams rather than filtering this.groups. In the client
    // path _loadClientGroups fetches `where type == "top"`, so this.groups
    // holds no teams at all and an in-memory filter silently finds none --
    // which is how this shipped clearing only the parent group.
    let teams = [];
    try {
      const snap = await getDocs(query(collection(db, "groups"), where("parent", "==", group.uuid)));
      teams = snap.docs.map((d) => d.data()).filter((t) => t && t.uuid);
    } catch (e) {
      console.error("Could not load teams for the AI cache clear:", e);
      this._showFeedback(t('groups.cacheTeamsLookupFailed', { name: group.name }));
      return;
    }

    const label = teams.length
      ? t('groups.nameAndTeams', { name: group.name, count: teams.length })
      : group.name;

    if (!confirm(t('groups.confirmClearCache', { label }))) {
      return;
    }

    this._clearingCacheUuid = group.uuid;
    try {
      // _clearAiSummariesForGroup swallows its own errors and returns null on
      // failure, so check the result rather than assuming success -- reporting
      // "Cleared" when nothing was deleted hides exactly the kind of problem
      // this button exists to fix.
      recordAudit(AUDIT.AI_CACHE_CLEAR, {
        subject: null,
        detail: { group: group.uuid, groupName: group.name, teams: teams.map((t) => t.uuid) },
      });
      const counts = [group.uuid, ...teams.map((t) => t.uuid)]
        .map((uuid) => this._clearAiSummariesForGroup(uuid));
      const results = await Promise.all(counts);

      if (results.some((r) => r === null)) {
        this._showFeedback(t('groups.cachePartialFailure', { label }));
      } else {
        const total = results.reduce((a, b) => a + b, 0);
        this._showFeedback(t('groups.cacheCleared', { label, count: total }));
      }
    } finally {
      this._clearingCacheUuid = null;
    }
  }

  // Stamps "due" with the current local time, in the same datetime-local
  // string format the Report Due field already stores (e.g. "2026-08-14T08:00"),
  // so it still displays correctly if the admin later opens the edit dialog.
  async _setDueToNow(group) {
    const now = new Date();
    const pad = n => String(n).padStart(2, '0');
    const due = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
    await updateDoc(doc(db, "groups", group.uuid), { due });
    group.due = due;
    this.requestUpdate();
  }

  // Non-empty string explains why bulk generation is blocked; empty means allowed.
  _bulkGenerationBlockedReason(group) {
    if (!group.due) return t('groups.noDueDate');
    if (new Date() < new Date(group.due)) {
      return t('groups.beforeDueDate', { date: formatDate(group.due, { dateStyle: 'medium', timeStyle: 'short' }) });
    }
    return '';
  }

  // For every member of the group: if they're on one or more child teams,
  // generate their AI Summary against each of those teams; otherwise
  // generate it against the group itself. Reuses any already-cached
  // summary (via generateAiSummary's own cache) and only generates what's
  // missing, then combines everything into one printable document sorted
  // by name. The combined document itself is never cached — every click
  // re-runs the whole process. Generation always runs in the context of
  // the student being summarized (targetEmail), never the admin clicking
  // the button — see functions/ai.js.
  async _generateAllSummaries(top) {
    if (this._bulkGeneratingUuid) return;
    this._bulkGeneratingUuid = top.uuid;
    this.requestUpdate();
    try {
      const results = await this._collectAllSummaries(top);
      if (!results) return;

      const meta = this._clientMeta[top.clientid];
      if (meta && meta.pdf_mode === PDF_MODE_BACKEND) {
        // Backend PDF rendering can produce either a single combined
        // document or a ZIP of individually-named PDFs -- ask which. There's
        // no such choice in browser-print mode (below), since a print
        // dialog can only ever produce the one combined document.
        this._pdfChoicePending = { group: top, results };
        return;
      }
      await this._deliverBulkSummaries(top, results);
    } catch (e) {
      console.error('Error generating bulk AI summaries:', e);
      alert(t('groups.bulkError', { message: e.message || e }));
    } finally {
      this._bulkGeneratingUuid = null;
      this.requestUpdate();
    }
  }

  // Called by the format-choice dialog's buttons once _generateAllSummaries
  // has parked a completed set of results in _pdfChoicePending.
  async _resolvePdfChoice(format) {
    const pending = this._pdfChoicePending;
    if (!pending) return;
    this._pdfChoicePending = null;
    if (format === 'zip') {
      await this._deliverBulkSummariesAsZip(pending.group, pending.results);
    } else {
      await this._deliverBulkSummaries(pending.group, pending.results);
    }
  }

  // Shared by _generateAllSummaries: does the actual per-member generation
  // and returns the results sorted by name, or null (having already told
  // the user why) if nothing could be generated.
  async _collectAllSummaries(top) {
    if (!top.due) {
      const proceed = confirm(t('groups.confirmSetDueNow'));
      if (!proceed) return null;
      await this._setDueToNow(top);
    }

    const blockedReason = this._bulkGenerationBlockedReason(top);
    if (blockedReason) {
      alert(blockedReason);
      return null;
    }

    // Reads every member's individual report to build the summaries (F-06).
    recordAudit(AUDIT.BULK_REPORT_READ, {
      subject: null,
      detail: { group: top.uuid, groupName: top.name, purpose: 'generate all AI summaries' },
    });

    const domainFiles = {
      m: await fetchStorageJson('domains/domain_message.json'),
      r: await fetchStorageJson('domains/domain_relationship.json'),
      t: await fetchStorageJson('domains/domain_time.json'),
      s: await fetchStorageJson('domains/domain_space.json'),
    };
    if (!domainFiles.m || !domainFiles.r || !domainFiles.t || !domainFiles.s) {
      throw new Error('Could not load domain reference data.');
    }

    const teamsSnap = await getDocs(query(
        collection(db, 'groups'),
        where('parent', '==', top.uuid),
        where('type', '==', 'team')
    ));
    const teams = teamsSnap.docs.map(d => d.data());

    // Group membership is denormalized onto the user doc, and includes
    // any child team the user also belongs to.
    const usersSnap = await getDocs(query(collection(db, 'users'), where('groups', 'array-contains', top.uuid)));
    const users = usersSnap.docs.map(d => d.data());

    const tasks = [];
    for (const u of users) {
      const memberTeams = teams.filter(t => Array.isArray(u.groups) && u.groups.includes(t.uuid));
      if (memberTeams.length > 0) {
        // A team can opt back out of AI features even while its parent
        // group has them on; skip those team members rather than
        // generating a summary for a team that disabled it.
        for (const t of memberTeams) {
          if (!isFlagOn(t.ai_enabled)) continue;
          tasks.push({user: u, groupId: t.uuid});
        }
      } else {
        tasks.push({user: u, groupId: top.uuid});
      }
    }

    if (tasks.length === 0) {
      alert(t('groups.noMembers'));
      return null;
    }

    const fn = httpsCallable(functions, 'generateAiSummary');
    // Cache holds in-flight promises (not just resolved values) so that
    // concurrent tasks sharing a groupId dedupe onto a single fetch
    // instead of each kicking off their own.
    const groupReportPromises = {};
    const fetchGroupReport = (groupId) => {
      if (!(groupId in groupReportPromises)) {
        groupReportPromises[groupId] = fetchStorageJson(`reports/groups/${groupId}.json`)
            .then((raw) => raw ? buildCombinedGroupReport(raw, domainFiles) : null);
      }
      return groupReportPromises[groupId];
    };

    const results = [];
    // Named so a partial run tells the admin who's missing and why, rather
    // than just a smaller-than-expected count with the reason buried in the
    // browser console (the previous behavior).
    const failures = [];
    const memberName = (u) => u.displayName || u.fullName
      || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email;
    const runTask = async (task) => {
      try {
        const userReportRaw = await fetchStorageJson(`reports/${task.user.email}.json`);
        if (!userReportRaw) {
          failures.push({ user: task.user, reason: t('groups.noIndividualReport') });
          return;
        }
        const combinedUserReport = buildCombinedReport(userReportRaw, domainFiles);

        const combinedGroupReport = await fetchGroupReport(task.groupId);
        if (!combinedGroupReport) {
          failures.push({ user: task.user, reason: t('groups.noGroupReport') });
          return;
        }

        const result = await fn({
          groupId: task.groupId,
          targetEmail: task.user.email,
          combinedUserReport,
          combinedGroupReport,
        });
        results.push({
          user: task.user,
          summary: result.data,
          contextTitle: combinedGroupReport.title,
          // Only teams have a parent group worth calling out -- its presence
          // is what marks this as a team-level (vs. group-level) report.
          parentGroupTitle: task.groupId !== top.uuid ? top.name : undefined,
          members: buildTeamMembers(combinedGroupReport.users),
        });
      } catch (e) {
        console.error(`Failed to generate summary for ${task.user.email}:`, e);
        failures.push({ user: task.user, reason: e.message || String(e) });
      }
    };

    // Bounded-concurrency worker pool: keeps AI_SUMMARY_CONCURRENCY tasks
    // in flight at once instead of awaiting them one at a time, without
    // firing all of them at once (which would blow past the Gemini API's
    // rate limit for a group of any size).
    let nextTaskIndex = 0;
    const worker = async () => {
      while (nextTaskIndex < tasks.length) {
        await runTask(tasks[nextTaskIndex++]);
      }
    };
    await Promise.all(
        Array.from({length: Math.min(AI_SUMMARY_CONCURRENCY, tasks.length)}, worker));

    if (results.length === 0) {
      const detail = failures.length
        ? '\n\n' + failures.map((f) => `${memberName(f.user)}: ${f.reason}`).join('\n')
        : '';
      alert(t('groups.noneGenerated') + detail);
      return null;
    }

    if (failures.length) {
      alert(
        t('groups.partialGenerated', { done: results.length, total: results.length + failures.length })
        + '\n\n'
        + failures.map((f) => `${memberName(f.user)} (${f.user.email}): ${f.reason}`).join('\n')
      );
    }

    results.sort((a, b) => {
      const nameA = `${a.user.lastName || ''}, ${a.user.firstName || ''}`.toLowerCase();
      const nameB = `${b.user.lastName || ''}, ${b.user.firstName || ''}`.toLowerCase();
      return nameA.localeCompare(nameB);
    });

    return results;
  }

  // `results` is [{ user, summary, contextTitle, parentGroupTitle }, ...] --
  // one already-generated AI summary per group/team member. Delivered either
  // as a browser print window (default) or a server-rendered PDF, per the
  // group's client's pdf_mode.
  async _deliverBulkSummaries(group, results) {
    const heading = t('groups.bulkHeading', { name: group.name });
    const meta = this._clientMeta[group.clientid];

    if (meta && meta.pdf_mode === PDF_MODE_BACKEND) {
      const sections = results.map(({user, summary, contextTitle, parentGroupTitle, members}) => ({
        summary, parentGroupTitle, contextTitle, members,
        heading: user.displayName || user.fullName ||
            `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
      }));
      try {
        await generateAndOpenSummaryPdf({documentTitle: heading, sections});
      } catch (e) {
        alert(t('ai.pdfFailed', { message: e.message || e }));
      }
      return;
    }

    const sections = results.map(({user, summary, contextTitle, parentGroupTitle, members}) => {
      const name = user.displayName || user.fullName ||
          `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email;
      return buildAiSummarySectionHtml(summary, {heading: name, contextTitle, parentGroupTitle, members});
    }).join('\n');

    const escapedHeading = escapeHtml(heading);
    const printHtml = `<!doctype html>
<html lang="${getLocale()}">
<head>
<meta charset="utf-8">
<title>${escapedHeading}</title>
<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Muli&family=Cormorant+Garamond">
<style>${AI_SUMMARY_PRINT_STYLE}</style>
</head>
<body>
<h1>${escapedHeading}</h1>
${sections}
</body>
</html>`;

    openPrintWindow(printHtml, {
      onBlocked: () => alert(t('ai.popupBlocked')),
    });
  }

  // `results` is the same shape _deliverBulkSummaries takes. Renders each
  // member's AI summary as its own PDF (filename = their email address)
  // and downloads all of them together in a single ZIP, via the backend
  // generateSummaryPdfZip callable. Only ever called when the group's
  // client uses PDF_MODE_BACKEND (see render()) — there's no client-side
  // equivalent of a server-rendered, individually-named PDF the way there
  // is for the combined browser-print document.
  async _deliverBulkSummariesAsZip(group, results) {
    const entries = results.map(({user, summary, contextTitle, parentGroupTitle, members}) => ({
      email: user.email,
      sections: [{
        summary, parentGroupTitle, contextTitle, members,
        heading: user.displayName || user.fullName ||
            `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
      }],
    }));
    try {
      await generateAndDownloadSummaryZip({documentTitle: t('groups.bulkHeading', { name: group.name }), entries});
    } catch (e) {
      alert(t('groups.zipFailed', { message: e.message || e }));
    }
  }

  async _loadGroups() {
    const snapshot = await getDocs(collection(db, "groups"));
    this.groups = snapshot.docs.map(doc => doc.data());
  }

    // Load top level groups for the client
    async _loadClientGroups(client) {
        console.log(`Loading groups for client ID: ${client.uuid}`);
        const groupsRef = collection(db, "groups");
        const q = query(
            groupsRef,
            where("clientid", "==", client.uuid),
            where("type", "==", "top")
        );

        try {
            const snapshot = await getDocs(q);
            const _groups = snapshot.docs.map(doc => doc.data());
            console.log(_groups);
            this.groups = _groups;
        } catch (error) {
            console.error("Error loading groups for " + client.uuid + ":", error);
            return [];
        }
    } 
    
    async _loadInvitationsForGroup(groupUuid) {
      if (!groupUuid) return [];

      const groupRef = doc(db, 'groups', groupUuid);
      const invitationsRef = collection(groupRef, 'invitations');
      const snapshot = await getDocs(invitationsRef);

      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    }

    _generateRandomCode() {
      const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
      const length = 8;
      const array = new Uint32Array(length);
      crypto.getRandomValues(array);

      return Array.from(array, x => chars[x % chars.length]).join('');
    }

  _openAddDialog() {
    this._editingGroup = null;
    this._groupFormData = this._getEmptyGroupData();
    this._isDialogOpen = true;
  }

  _openEditDialog(group) {
    this._editingGroup = group;
    this._groupFormData = { ...group };
    this._isDialogOpen = true;
  }

  _closeDialog() {
    this._isDialogOpen = false;
    this._groupFormData = this._getEmptyGroupData();
  }

  _handleFormInput(e) {
    const target = e.target;
    let { name, value } = target;
    console.log(name + ":" + value);
    if (!name) {
        name = target.dataset.name;
    }
    // Trim whitespace if value is a string
    if (typeof value === 'string') {
      value = value.trim();
    }
    this._groupFormData = { ...this._groupFormData, [name]: value };
    if (name == 'payment_amount') {
        let pValue = value.replace(/[^0-9.]/g, ""); // Remove non-numeric and non-decimal characters
        if (pValue.includes(".")) {
            const parts = pValue.split(".");
            this._groupFormData[name] = Number(parts[0] + "." + parts[1].substring(0, 2)); // Limit to two decimal places
        } else {
            this._groupFormData[name] = Number(pValue + ".00");
        }          
    }
  }

  async _saveGroup() {
    const group = { ...this._groupFormData };
    if (!group.uuid) group.uuid = uuidv4();
    const previous = this._editingGroup;
    const groupRef = doc(db, "groups", group.uuid);
    await setDoc(groupRef, group);
    if (group.type === 'top') {
      await this._cascadeAiEnabledToTeams(group);
    }
    this._showFeedback(t('groups.saved', { name: group.name }));
    this._closeDialog();
    this._loadClientGroups(this.client);

    // A changed privacy setting or due date can make an already-cached AI
    // Summary stale (wrong names shown, or wrong responses counted), so
    // clear the cache and let it regenerate under the new settings.
    if (previous && (group.disable_report_privacy != previous.disable_report_privacy ||
        group.due !== previous.due)) {
      await this._clearAiSummariesForGroup(group.uuid);
    }
  }

  // Keeps a team's AI toggle from silently drifting out of sync with its
  // parent group -- a team can still be flipped off individually afterward,
  // but the group is the source of truth whenever it's edited.
  async _cascadeAiEnabledToTeams(group) {
    const teamsSnap = await getDocs(query(
        collection(db, 'groups'),
        where('parent', '==', group.uuid),
        where('type', '==', 'team')
    ));
    await Promise.all(teamsSnap.docs.map(d =>
        updateDoc(doc(db, 'groups', d.id), { ai_enabled: group.ai_enabled })));
  }

  async _deleteGroup(group) {
    if (confirm(t('groups.confirmDelete', { name: group.name }))) {
      await deleteDoc(doc(db, "groups", group.uuid));
      this._showFeedback(t('groups.deleted', { name: group.name }));
      this._loadClientGroups(this.client);
    }
  }

  _showFeedback(message) {
    this._feedbackMessage = message;
    alert(message);
//    this.shadowRoot.getElementById('feedback-snackbar').open();
  }

  // ---- Invitation Template ----

  _openTemplateDialog(group) {
    this._templateGroup = group;
    this._templateDialogOpen = true;
  }

  _closeTemplateDialog() {
    this._templateDialogOpen = false;
    this._templateGroup = null;
  }

  static get styles() {
    return [a11yStyles, css`
      .group-item {
        width: 100%;
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        align-items: flex-start;
        gap: 8px;
        padding-bottom: 10px;
        border-bottom: 1px solid #e0e0e0;
      }
      .group-item > div:first-child {
        flex: 1 1 220px;
        min-width: 0;
      }
      .actions {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        flex: 0 0 auto;
        gap: 4px;
      }
      .actions mwc-button {
        margin-left: 0;
        --mdc-typography-button-font-size: 0.72rem;
        --mdc-button-height: 28px;
      }
        .group-name {
            font-weight: bold;
        }
        .client-institution {
            font-size: 0.9em;
            color: #555;
        }
         .group-description, .client-address {
             font-size: 0.85em;
             color: #6b6b6b;
        }

      /* Was a styled <div>; a real heading gives the admin panels a
         navigable structure (WCAG 1.3.1). */
      .section-header {
        margin: 0 0 5px 0;
        width: 100%;
        box-sizing: border-box;
        color: black;
        font-size: 1.5em;
        font-weight: normal;
        padding: 15px 10px 10px 30px;
        background-color: gray;
      }
      .section-header .disclosure {
        vertical-align: middle;
        margin-left: 8px;
        padding: 4px;
      }
    `];
  }

  firstUpdated(props) {
    if (this.client) {
        this._loadClientGroups(this.client);
    } else {
        if (this.mode == 'trainer') {
            // do nothing... groups should have loaded...
        } else if (this.mode == 'pm') {
            // do nothing... groups should have loaded...
        } else {
            // test to make sure user is admin
            this._loadGroups();
        }
    }
  }
  
  // Shared by the trainer/pm accordion and the plain client-mode view.
  _renderGroupsList(topGroups, teamGroups) {
    if (this._groupUuid) {
      const invitedGroup = topGroups.find(g => g.uuid === this._groupUuid);
      return html`
        <h3>
          Invitations for ${invitedGroup ? invitedGroup.name : ''}
          <mwc-button dense outlined label=${t('common.back')} @click=${() => this._groupUuid = undefined}></mwc-button>
        </h3>
        <intrivity-admin-invitations .mode=${this.mode} .user_profile=${this.user_profile} .groupUuid=${this._groupUuid}></intrivity-admin-invitations>
      `;
    }
    if (this._selectedUsersGroup) {
      return html`
        <h3>
          Users of ${this._selectedUsersGroup.name}
          <mwc-button dense outlined label=${t('common.back')} @click=${() => this._selectedUsersGroup = undefined}></mwc-button>
        </h3>
        <intrivity-admin-users .mode=${this.mode} .client=${this.client} .group=${this._selectedUsersGroup} .user_profile=${this.user_profile}></intrivity-admin-users>
      `;
    }
    if (this._selectedGroup) {
      return html`
        <h3>
          Teams of ${this._selectedGroup.name}
          <mwc-button dense outlined label=${t('common.back')} @click=${() => this._selectedGroup = undefined}></mwc-button>
        </h3>
        <intrivity-admin-teams .mode=${this.mode} .client=${this.client} .group=${this._selectedGroup} .user_profile=${this.user_profile}></intrivity-admin-teams>
      `;
    }
    return html`
      ${topGroups.length === 0 ? html`
        <p>${t('groups.none')}</p>
      ` : html`
              <mwc-list>
                  ${topGroups.sort((a, b) => a.name.localeCompare(b.name)).map(top => html`
                  <mwc-list-item style="display: block; width: auto; height: auto;">
                    <div class="group-item">
                      <div style="min-width: 0;">
                        <div class="group-name"><strong>${top.name}</strong></div>
                        <div style="font-size: 0.7em; color: #6b6b6b;">${top.uuid}</div>
                        <div class="group-description">${top.description || t('groups.noDescription')}</div>
                        <div class="group-description">${top.code ? t('groups.joinCode', { code: top.code }) : t('groups.noJoinCode')}</div>
                        <div class="group-description">${top.payment_amount > -1 ? formatNumber(Number(top.payment_amount), { style: 'currency', currency: 'USD' }) : t('groups.noPaymentAmount')}</div>
                        <div class="group-description">${top.payment_required ? t('groups.paymentRequired') : t('groups.noPaymentRequired')}</div>
                      </div>
                      <div class="actions">
                        <mwc-button dense outlined label=${t('groups.invitations')} @click=${() => this._groupUuid = top.uuid}></mwc-button>
                        ${this._aiEnabledForGroup(top) ? html`
                        <mwc-button
                          dense
                          outlined
                          label=${this._bulkGeneratingUuid === top.uuid ? t('groups.generating') : t('groups.generateAll')}
                          ?disabled=${this._bulkGeneratingUuid === top.uuid}
                          title=${this._bulkGenerationBlockedReason(top) || ''}
                          style=${this._bulkGenerationBlockedReason(top) ? 'opacity: 0.6;' : ''}
                          @click=${() => this._generateAllSummaries(top)}
                        ></mwc-button>
                        <mwc-button
                          dense
                          outlined
                          label=${this._clearingCacheUuid === top.uuid ? t('groups.clearing') : t('groups.clearCache')}
                          ?disabled=${this._clearingCacheUuid === top.uuid || this._bulkGeneratingUuid === top.uuid}
                          title=${t('groups.clearCacheTitle')}
                          @click=${() => this._clearAiCacheForGroupAndTeams(top)}
                        ></mwc-button>
                        `: html``}
                        ${ this.mode == 'client' ? html`
                            <mwc-button dense outlined label=${t('groups.users')} @click=${() => this._selectedUsersGroup = top}></mwc-button>
                            <mwc-button dense outlined label=${t('groups.teams')} @click=${() => this._selectedGroup = top}></mwc-button>
                            <mwc-button dense outlined label=${t('common.edit')} @click=${() => this._openEditDialog(top)}></mwc-button>
                            <mwc-button dense outlined label=${t('groups.editInvitation')} @click=${() => this._openTemplateDialog(top)}></mwc-button>
                            ${hasRole('superadmin', this.user_profile) ? html`<mwc-button dense outlined label=${t('common.delete')} @click=${() => this._deleteGroup(top)}></mwc-button>`: html``}
                        `: html`
                            <mwc-button dense outlined label=${top.disable_report_privacy ? t('groups.privacyOff') : t('groups.privacyOn')} @click=${() => this._toggleReportPrivacy(top)}></mwc-button>
                            ${this.mode == 'pm' ? html`
                                <mwc-button dense outlined label=${t('groups.teams')} @click=${() => this._selectedGroup = top}></mwc-button>
                                <mwc-button dense outlined label=${t('common.edit')} @click=${() => this._openEditDialog(top)}></mwc-button>
                            `: html``}
                        `}
                        </div>
                    </div>
                  </mwc-list-item>

                  ${teamGroups
                    .filter(team => team.parent === top.uuid)
                    .map((team, index) => html`
                        ${index === 0 ? html`
                            <div style="margin-left: 20px; font-weight: bold; padding: 5px 0;">${t('groups.teams')}</div>
                      ` : ''}
                      <mwc-list-item style="display: block; width: auto; height: auto; margin-left: 20px;">
                        <div class="group-item">
                          <div style="min-width: 0;">
                            <div class="group-name">${team.name}</div>
                            <div style="font-size: 0.7em; color: #6b6b6b;">${team.uuid}</div>
                            <div class="group-description">${team.description || t('groups.noDescription')}</div>
                          </div>
                          <div class="actions">
                            <mwc-button dense outlined label=${t('common.edit')} @click=${() => this._openEditDialog(team)}></mwc-button>
                            ${hasRole('superadmin', this.user_profile) ? html`<mwc-button dense outlined label=${t('common.delete')} @click=${() => this._deleteGroup(team)}></mwc-button>`: html``}
                          </div>
                        </div>
                      </mwc-list-item>
                    `)}
                `)}



              </mwc-list>
      `}
    `;
  }

  render() {

    const topGroups =
       this.mode == 'trainer' ? this.groups :
                this.groups
                    .filter(group => group.type === 'top')
                    .sort((a, b) => a.name.localeCompare(b.name));

    const teamGroups =
        this.mode == 'trainer' ? [] :
                this.groups
                    .filter(group => group.type === 'team')
                    .sort((a, b) => a.name.localeCompare(b.name));

    // Program managers get a scoped edit dialog: only due dates, trainer
    // visibility, report privacy, and AI features (and AI only if the
    // client itself has AI features on) -- everything else in the dialog
    // is shown read-only for context, not editable.
    const pmRestricted = this.mode == 'pm';
    const pmCanEditAi = !pmRestricted || !!(this._clientMeta[this._groupFormData.clientid] && this._clientMeta[this._groupFormData.clientid].ai_enabled);

    return html`
      ${this.mode == 'trainer' || this.mode == 'pm' ? html`
          <h2 class="section-header">
                  ${this.mode == 'trainer' ? t('groups.trainerGroups') : t('groups.pmGroups')}
                  ${disclosureButton(this._expanded, this.mode == 'trainer' ? t('groups.trainerGroups') : t('groups.pmGroups'), this._panelId, () => { this._expanded = !this._expanded; })}
          </h2>
          ${this._expanded ? html`
              <h3>${this.mode == 'trainer' ? t('groups.welcomeTrainer') : t('groups.welcomePm')}</h3>
              ${this._renderGroupsList(topGroups, teamGroups)}
          `: html``}
      `: html`
          <h3>${t('groups.groupsOf', { name: this.client.name })} <mwc-button outlined label=${t('groups.allClients')} @click=${(e)=> {this.dispatchEvent(new CustomEvent(INTRIVITY_CLIENTS_GROUPS_CLOSED, { bubbles: true, composed: true }))} }></mwc-button></h3>
          ${hasRole('superadmin', this.user_profile) && !this._groupUuid && !this._selectedUsersGroup && !this._selectedGroup ? html`<mwc-button raised label=${t('groups.add')} @click=${this._openAddDialog}></mwc-button>`: html``}
          ${this._renderGroupsList(topGroups, teamGroups)}
      `}

      <mwc-dialog heading=${t('groups.downloadHeading')} ?open=${!!this._pdfChoicePending} @closed=${() => this._pdfChoicePending = null}>
        <div>
          ${t('groups.downloadQuestion')}
          <strong>${this._pdfChoicePending ? this._pdfChoicePending.group.name : ''}</strong>
        </div>
        <mwc-button slot="secondaryAction" label=${t('groups.individualPdfs')} @click=${() => this._resolvePdfChoice('zip')}></mwc-button>
        <mwc-button slot="secondaryAction" label=${t('groups.combinedPdf')} @click=${() => this._resolvePdfChoice('pdf')}></mwc-button>
        <mwc-button slot="primaryAction" dialogAction="cancel" label=${t('common.cancel')}></mwc-button>
      </mwc-dialog>

      <mwc-dialog heading="${this._editingGroup ? t('groups.edit') : t('groups.add')}" ?open=${this._isDialogOpen} @closed=${this._closeDialog}>
        <mwc-textfield required style="width: 100%;" label=${t('common.name')} name="name" ?disabled=${pmRestricted} .value=${this._groupFormData.name} @input=${this._handleFormInput}></mwc-textfield>
        <mwc-textarea style="width: 100%;" label=${t('common.description')} name="description" ?disabled=${pmRestricted} .value=${this._groupFormData.description} @input=${this._handleFormInput}></mwc-textarea>
        <div style="width: 100%; display: grid; grid-template-columns: 1fr 1fr;">
            ${pmRestricted ? html`` : html`
            <mwc-textfield label=${t('groups.surveyMonkeyKey')} name="survey_monkey_key" .value=${this._groupFormData.survey_monkey_key} @input=${this._handleFormInput}></mwc-textfield>
            <mwc-select style="width: 100%;" data-name="status" name="status" label=${t('groups.reportStatus')}
                .value=${this._groupFormData.status} @selected=${this._handleFormInput} @closed=${e => e.stopPropagation()}>
                    <mwc-list-item value="none" ?selected=${this._groupFormData.status === 'none'}>${t('groups.statusNone')}</mwc-list-item>
                    <mwc-list-item value="process" ?selected=${this._groupFormData.status === 'process'}>${t('groups.statusProcess')}</mwc-list-item>
                    <mwc-list-item value="processed" ?selected=${this._groupFormData.status === 'processed'}>${t('groups.statusReady')}</mwc-list-item>
            </mwc-select>
            `}
            <mwc-textfield label=${t('groups.code')} name="code" ?disabled=${pmRestricted} .value=${this._groupFormData.code} @input=${this._handleFormInput}></mwc-textfield>
            <mwc-textfield label=${t('groups.paymentAmount')} name="payment_amount" type="text" inputmode="decimal" prefix="$" ?disabled=${pmRestricted} .value=${this._groupFormData.payment_amount ? (this._groupFormData.payment_amount.toString() || '0.00'): '0.00'} @input=${this._handleFormInput}></mwc-textfield>
            <mwc-textfield style="width: 100%" type="date" label=${t('field.stopDate')} name="stop_date" .value=${this._groupFormData.stop_date} @input=${this._handleFormInput}></mwc-textfield>
            <mwc-textfield style="width: 100%" type="datetime-local" label=${t('groups.reportDueLong')} name="due" .value=${this._groupFormData.due || ''} @input=${this._handleFormInput}></mwc-textfield>
            <label style="grid-column: span 2; margin: 8px 0;">
              ${t('invitationLanguage.groupDefaultLabel')}
              <select @change=${(e) => { this._groupFormData = { ...this._groupFormData, language: e.target.value }; }}>
                <option value="" ?selected=${!this._groupFormData.language}>${t('invitationLanguage.clientDefault')}</option>
                ${Object.keys(LOCALES).map((code) => html`
                  <option value=${code} ?selected=${this._groupFormData.language === code}>${LOCALES[code].name}</option>
                `)}
              </select>
            </label>
            <mwc-select style="width: 100%;" data-name="payment_required" name="payment_required" label=${t('groups.paymentRequired')} ?disabled=${pmRestricted}
                .value=${this._groupFormData.payment_required} @selected=${this._handleFormInput} @closed=${e => e.stopPropagation()}>
                ${[1, 0].map( s => html`<mwc-list-item value=${s} ?selected=${this._groupFormData.payment_required == s}>${s==0 ? t('common.false') : t('common.true') }</mwc-list-item>`)}
            </mwc-select>
            ${pmRestricted ? html`` : html`
            <mwc-select style="width: 100%;" data-name="group_enabled" name="group_enabled" label=${t('groups.enabled')}
                .value=${this._groupFormData.group_enabled} @selected=${this._handleFormInput} @closed=${e => e.stopPropagation()}>
                ${[1, 0].map( s => html`<mwc-list-item value=${s} ?selected=${this._groupFormData.group_enabled == s}>${s==0 ? t('common.false') : t('common.true') }</mwc-list-item>`)}
            </mwc-select>
            <mwc-select style="width: 100%;" data-name="printable_report" name="printable_report" label=${t('groups.enablePrintable')}
                .value=${this._groupFormData.printable_report} @selected=${this._handleFormInput} @closed=${e => e.stopPropagation()}>
                ${[1, 0].map( s => html`<mwc-list-item value=${s} ?selected=${this._groupFormData.printable_report == s}>${s==0 ? t('common.false') : t('common.true') }</mwc-list-item>`)}
            </mwc-select>
            `}
            <mwc-select style="width: 100%;" data-name="ai_enabled" name="ai_enabled" label=${t('field.enableAi')}
                ?disabled=${!pmCanEditAi}
                title=${!pmCanEditAi ? t('groups.clientAiDisabled') : ''}
                .value=${this._groupFormData.ai_enabled} @selected=${this._handleFormInput} @closed=${e => e.stopPropagation()}>
                ${[1, 0].map( s => html`<mwc-list-item value=${s} ?selected=${this._groupFormData.ai_enabled == s}>${s==0 ? t('common.false') : t('common.true') }</mwc-list-item>`)}
            </mwc-select>
            ${pmRestricted ? html`` : html`
            <mwc-select style="width: 100%;" data-name="norm_groups" name="norm_groups" label=${t('groups.enableNorm')}
                .value=${this._groupFormData.norm_groups} @change=${this._handleFormInput} @closed=${e => e.stopPropagation()}>
                ${[1, 0].map( s => html`<mwc-list-item value=${s} ?selected=${this._groupFormData.norm_groups == s}>${s==0 ? t('common.false') : t('common.true') }</mwc-list-item>`)}
            </mwc-select>
            <mwc-select style="width: 100%;" data-name="group_compare" name="group_compare" label=${t('groups.enableCompare')}
                .value=${this._groupFormData.group_compare} @selected=${this._handleFormInput} @closed=${e => e.stopPropagation()}>
                ${[1, 0].map( s => html`<mwc-list-item value=${s} ?selected=${this._groupFormData.group_compare == s}>${s==0 ? t('common.false') : t('common.true') }</mwc-list-item>`)}
            </mwc-select>
            <mwc-select style="width: 100%;" data-name="report" name="report" label=${t('groups.enableReport')}
                .value=${this._groupFormData.report} @selected=${this._handleFormInput} @closed=${e => e.stopPropagation()}>
                ${[1, 0].map( s => html`<mwc-list-item value=${s} ?selected=${this._groupFormData.report == s}>${s==0 ? t('common.false') : t('common.true') }</mwc-list-item>`)}
            </mwc-select>
            <mwc-select style="width: 100%;" data-name="joining" name="joining" label=${t('groups.enableJoining')}
                .value=${this._groupFormData.joining} @selected=${this._handleFormInput} @closed=${e => e.stopPropagation()}>
                ${[1, 0].map( s => html`<mwc-list-item value=${s} ?selected=${this._groupFormData.joining == s}>${s==0 ? t('common.false') : t('common.true') }</mwc-list-item>`)}
            </mwc-select>
            `}
        </div>
        <mwc-select style="width: 100%;" data-name="allow_trainers" name="allow_trainers" label=${t('field.allowTrainers')}
            .value=${this._groupFormData.allow_trainers} @selected=${this._handleFormInput} @closed=${e => e.stopPropagation()}>
            ${[1, 0].map( s => html`<mwc-list-item value=${s} ?selected=${this._groupFormData.allow_trainers == s}>${s==0 ? t('common.false') : t('common.true') }</mwc-list-item>`)}
        </mwc-select>
        <mwc-select style="width: 100%;" data-name="disable_report_privacy" name="disable_report_privacy" label=${t('groups.disablePrivacyLong')}
            .value=${this._groupFormData.disable_report_privacy} @selected=${this._handleFormInput} @closed=${e => e.stopPropagation()}>
            ${[1, 0].map( s => html`<mwc-list-item value=${s} ?selected=${this._groupFormData.disable_report_privacy == s}>${s==0 ? t('common.false') : t('common.true') }</mwc-list-item>`)}
        </mwc-select>
        <mwc-select style="width: 100%;" data-name="restrict_team_comparison" name="restrict_team_comparison" label=${t('groups.restrictComparison')}
            .value=${this._groupFormData.restrict_team_comparison} @selected=${this._handleFormInput} @closed=${e => e.stopPropagation()}>
            ${[1, 0].map( s => html`<mwc-list-item value=${s} ?selected=${this._groupFormData.restrict_team_comparison == s}>${s==0 ? t('common.false') : t('common.true') }</mwc-list-item>`)}
        </mwc-select>
        <mwc-textfield required style="width: 100%;" disabled label=${t('groups.clientId')} name="clientid" .value=${this._groupFormData.clientid} @input=${this._handleFormInput}></mwc-textfield>
        ${this._editingGroup && this._editingGroup.uuid ? html`
                <mwc-textfield style="width: 100%;"
                    label=${t('groups.groupId')}
                    name="uuid"
                    .value=${this._editingGroup.uuid}
                    readonly
                    disabled>
                </mwc-textfield>
             ` : 
             html`
                <mwc-textfield style="width: 100%;"
                    label=${t('groups.groupId')}
                    name="uuid"
                    .value=${this._groupFormData.uuid || ''}
                    @input=${this._handleFormInput}
                    ?disabled=${this._isLoading}
                    required >
                </mwc-textfield>
             `              
        }        
        <mwc-button slot="primaryAction" label=${t('common.save')} @click=${this._saveGroup}></mwc-button>
        <mwc-button slot="secondaryAction" dialogAction="cancel" label=${t('common.cancel')}></mwc-button>
      </mwc-dialog>

      <intrivity-invitation-template-dialog
          collection="groups"
          label=${t('groups.group')}
          .target=${this._templateGroup}
          .parentTemplate=${this.client && this.client.invitation_template}
          .open=${this._templateDialogOpen}
          @dialog-closed=${this._closeTemplateDialog}>
      </intrivity-invitation-template-dialog>

      <mwc-snackbar id="feedback-snackbar" labelText=${this._feedbackMessage}></mwc-snackbar>
    `;
  }
}

customElements.define('intrivity-admin-groups', IntrivityAdminGroups);
